import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaLinkedin } from 'react-icons/fa';
import { Container, Section, Button, theme } from '../styles/GlobalStyles';

const ContactHeader = styled.section`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #003d7a 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const HeaderTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const HeaderSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactForm = styled(motion.form)`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: ${theme.shadows.large};
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.colors.dark};
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${theme.colors.dark};
  margin-bottom: 8px;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.lightGray};
  border-radius: 8px;
  font-size: 1rem;
  transition: all ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 82, 164, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.lightGray};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: all ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 82, 164, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.lightGray};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 82, 164, 0.1);
  }
`;

const ContactInfo = styled(motion.div)``;

const InfoCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 30px;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.dark};
  margin-bottom: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  width: 45px;
  height: 45px;
  background: ${theme.colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.dark};
  margin-bottom: 5px;
`;

const InfoText = styled.p`
  color: ${theme.colors.gray};
  line-height: 1.5;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  background: ${theme.colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
  };

  return (
    <>
      <ContactHeader>
        <Container>
          <HeaderTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Get in Touch
          </HeaderTitle>
          <HeaderSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Ready to enhance your cybersecurity posture? Contact our experts for a consultation.
          </HeaderSubtitle>
        </Container>
      </ContactHeader>

      <Section>
        <Container>
          <ContactGrid>
            <ContactForm
              onSubmit={handleSubmit}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <FormTitle>Send us a Message</FormTitle>
              
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormGrid>

              <FormGrid>
                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>
              </FormGrid>

              <FormGrid>
                <FormGroup>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="service">Service Interest</Label>
                  <Select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service</option>
                    <option value="compliance">Compliance Services</option>
                    <option value="risk-assessment">Risk Assessment</option>
                    <option value="penetration-testing">Penetration Testing</option>
                    <option value="managed-services">Managed Services</option>
                    <option value="security-technologies">Security Technologies</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <FormGroup className="full-width">
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your cybersecurity needs..."
                  required
                />
              </FormGroup>

              <Button type="submit" variant="primary" style={{ width: '100%', padding: '15px' }}>
                Send Message
              </Button>
            </ContactForm>

            <ContactInfo
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <InfoCard>
                <InfoTitle>Contact Information</InfoTitle>
                
                <InfoItem>
                  <InfoIcon><FaPhone /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoText>+1 (555) 123-4567</InfoText>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon><FaEnvelope /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>Email</InfoLabel>
                    <InfoText>info@cyhy360.com</InfoText>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon><FaMapMarkerAlt /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>Address</InfoLabel>
                    <InfoText>123 Security Blvd<br />Cyber City, CC 12345</InfoText>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon><FaClock /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>Business Hours</InfoLabel>
                    <InfoText>Monday - Friday: 9:00 AM - 6:00 PM<br />Saturday: 10:00 AM - 4:00 PM</InfoText>
                  </InfoContent>
                </InfoItem>

                <SocialLinks>
                  <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                    <FaLinkedin />
                  </SocialLink>
                </SocialLinks>
              </InfoCard>

              <InfoCard>
                <InfoTitle>Why Choose CYHY360?</InfoTitle>
                <InfoText style={{ marginBottom: '15px' }}>
                  With years of experience in cybersecurity consulting, we provide:
                </InfoText>
                <ul style={{ paddingLeft: '20px', color: theme.colors.gray }}>
                  <li style={{ marginBottom: '8px' }}>Expert guidance from certified professionals</li>
                  <li style={{ marginBottom: '8px' }}>Comprehensive security solutions</li>
                  <li style={{ marginBottom: '8px' }}>24/7 support and monitoring</li>
                  <li style={{ marginBottom: '8px' }}>Proven track record of success</li>
                </ul>
              </InfoCard>
            </ContactInfo>
          </ContactGrid>
        </Container>
      </Section>
    </>
  );
};

export default Contact;

